import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';
import { auditLogService } from '@/services/auditLogService';

// Standard Audit Types
export const AUDIT_TYPES = [
  'SOC2',
  'ISO27001',
  'HIPAA',
  'PCI-DSS',
  'GDPR',
  'NIST',
  'SOX',
  'FISMA',
  'FedRAMP',
  'CMMC',
  'Custom'
] as const;

export type AuditType = typeof AUDIT_TYPES[number];

// Audit Template Interface
export interface AuditTemplate {
  id?: string;
  name: string;
  description?: string;
  auditType: string; // Standard types: SOC2, ISO27001, HIPAA, etc. or "Custom"
  frequency: 'yearly' | 'semi-yearly' | 'quarterly';
  complianceTemplateIds: string[]; // Array of compliance template IDs
  organizationId: string;
  createdBy?: string; // User ID
  createdAt?: any; // Firestore Timestamp
  updatedAt?: any; // Firestore Timestamp
  isActive?: boolean; // Soft delete flag
  metadata?: {
    industry?: string; // e.g., "Healthcare", "Finance", "Technology"
    jurisdiction?: string; // e.g., "US", "EU", "Global"
    aiGenerated?: boolean; // Flag if created via AI
    sourceQuery?: string; // Original AI query if AI-generated
    customAuditType?: string; // Temporary field for custom audit type input
    customAuditTypeName?: string; // Stored custom audit type name when auditType is "Custom"
  };
}

// Audit Finding Interface
export interface AuditFinding {
  id: string;
  auditId: string; // Reference to the audit
  complianceTemplateId: string;
  complianceTemplateName: string;
  taskId?: string; // If associated with a task
  status: 'compliant' | 'non-compliant' | 'partial' | 'not_applicable';
  notes?: string;
  evidenceUrls?: string[];
  reviewedBy?: string;
  reviewedAt?: any; // Firestore Timestamp
  createdAt?: any; // Firestore Timestamp
  updatedAt?: any; // Firestore Timestamp
}

export interface AuditEvidence {
  id: string;
  auditId: string;
  complianceTemplateId: string; // Which compliance template this evidence supports
  evidenceType: 'task_completion' | 'checklist' | 'form' | 'direct_upload';
  sourceId?: string; // taskId, checklistId, formId, or null for direct upload
  sourceName?: string; // Human-readable name of the source
  url: string; // File URL or reference URL
  fileName?: string; // Original filename for direct uploads
  uploadedAt?: any; // Firestore Timestamp
  uploadedBy?: string; // User ID
  description?: string; // Optional description
  createdAt?: any; // Firestore Timestamp
}

// Audit Instance Interface
export interface Audit {
  id?: string;
  auditTemplateId: string; // Reference to audit_templates
  auditTemplateName: string; // Denormalized for performance
  locationId: string; // Reference to locations
  locationName: string; // Denormalized for performance
  organizationId: string;
  
  // Scheduling
  scheduledDate: any; // Firestore Timestamp - when audit should be conducted
  dueDate: any; // Firestore Timestamp - deadline for completion
  frequency: 'yearly' | 'semi-yearly' | 'quarterly';
  nextAuditDate?: any; // Firestore Timestamp - for recurring audits
  
  // Status tracking
  status: 'planned' | 'in_progress' | 'completed' | 'overdue' | 'cancelled';
  startedAt?: any; // Firestore Timestamp
  completedAt?: any; // Firestore Timestamp
  completedBy?: string; // User ID
  
  // Compliance tracking
  associatedTaskIds: string[]; // Tasks that contribute to this audit
  complianceTemplateIds: string[]; // Compliance templates required for this audit
  completionPercentage: number; // 0-100, calculated from task completion
  
  // Audit results
  findings?: AuditFinding[];
  notes?: string;
  evidenceUrls?: string[]; // Legacy: Links to evidence documents (deprecated, use evidence array)
  evidence?: AuditEvidence[]; // Structured evidence array
  
  createdAt?: any; // Firestore Timestamp
  updatedAt?: any; // Firestore Timestamp
}

// User info type for audit logging
interface UserInfo {
  id: string;
  name: string;
  email: string;
}

// Audit service
export const auditService = {
  // ========== AUDIT TEMPLATE OPERATIONS ==========
  
  // Get all audit templates for an organization
  getAllTemplates: async (organizationId: string) => {
    try {
      const templatesRef = collection(db, 'audit_templates');
      const q = query(
        templatesRef,
        where('organizationId', '==', organizationId),
        where('isActive', '!=', false) // Get active templates (isActive can be true or undefined)
      );
      
      const snapshot = await getDocs(q);
      const templates: AuditTemplate[] = [];
      
      snapshot.forEach((doc) => {
        const templateData = doc.data();
        templates.push({ 
          id: doc.id, 
          ...templateData 
        } as AuditTemplate);
      });
      
      // Sort by name
      templates.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      return { success: true, data: templates };
    } catch (error: any) {
      console.error('Error getting audit templates:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message };
    }
  },

  // Get single audit template by ID
  getTemplate: async (templateId: string) => {
    try {
      const templateRef = doc(db, 'audit_templates', templateId);
      const snapshot = await getDoc(templateRef);
      
      if (snapshot.exists()) {
        const templateData = snapshot.data();
        return { 
          success: true, 
          data: { id: snapshot.id, ...templateData } as AuditTemplate 
        };
      } else {
        return { success: false, error: 'Audit template not found' };
      }
    } catch (error: any) {
      console.error('Error getting audit template:', error);
      return { success: false, error: error.message };
    }
  },

  // Create new audit template
  createTemplate: async (
    template: Omit<AuditTemplate, 'id' | 'createdAt' | 'updatedAt'>,
    userInfo?: UserInfo
  ) => {
    try {
      const templatesRef = collection(db, 'audit_templates');
      const templateData: any = {
        ...template,
        isActive: template.isActive !== undefined ? template.isActive : true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // Remove undefined values
      Object.keys(templateData).forEach((key) => {
        if (templateData[key] === undefined) {
          delete templateData[key];
        }
      });
      
      const docRef = await addDoc(templatesRef, templateData);
      
      // Log audit entry
      if (userInfo && template.organizationId) {
        await auditLogService.log({
          action: 'audit_template_added',
          entityType: 'audit_template',
          entityId: docRef.id,
          entityName: template.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: template.organizationId,
        });
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error creating audit template:', error);
      return { success: false, error: error.message };
    }
  },

  // Update audit template
  updateTemplate: async (
    templateId: string,
    updates: Partial<AuditTemplate>,
    userInfo?: UserInfo
  ) => {
    try {
      const templateRef = doc(db, 'audit_templates', templateId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      await updateDoc(templateRef, updateData);
      
      // Log audit entry
      if (userInfo) {
        // Get template name for logging
        const templateSnap = await getDoc(templateRef);
        const templateName = templateSnap.exists() 
          ? templateSnap.data().name 
          : 'Unknown';
        
        await auditLogService.log({
          action: 'audit_template_updated',
          entityType: 'audit_template',
          entityId: templateId,
          entityName: templateName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: updates.organizationId || templateSnap.data()?.organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating audit template:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete audit template (soft delete)
  deleteTemplate: async (
    templateId: string,
    userInfo?: UserInfo
  ) => {
    try {
      const templateRef = doc(db, 'audit_templates', templateId);
      
      // Get template data for logging
      const templateSnap = await getDoc(templateRef);
      if (!templateSnap.exists()) {
        return { success: false, error: 'Audit template not found' };
      }
      
      const templateData = templateSnap.data();
      
      // Soft delete by setting isActive to false
      await updateDoc(templateRef, {
        isActive: false,
        updatedAt: Timestamp.now()
      });
      
      // Log audit entry
      if (userInfo) {
        await auditLogService.log({
          action: 'audit_template_deleted',
          entityType: 'audit_template',
          entityId: templateId,
          entityName: templateData.name || 'Unknown',
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: templateData.organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting audit template:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== AUDIT INSTANCE OPERATIONS ==========
  
  // Get all audits for an organization
  getAuditsForOrganization: async (organizationId: string) => {
    try {
      const auditsRef = collection(db, 'audits');
      const q = query(
        auditsRef,
        where('organizationId', '==', organizationId),
        orderBy('scheduledDate', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const audits: Audit[] = [];
      
      snapshot.forEach((doc) => {
        const auditData = doc.data();
        audits.push({ 
          id: doc.id, 
          ...auditData 
        } as Audit);
      });
      
      return { success: true, data: audits };
    } catch (error: any) {
      console.error('Error getting audits:', error);
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message };
    }
  },

  // Get audits for a specific location
  getAuditsForLocation: async (locationId: string) => {
    try {
      const auditsRef = collection(db, 'audits');
      const q = query(
        auditsRef,
        where('locationId', '==', locationId),
        orderBy('scheduledDate', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const audits: Audit[] = [];
      
      snapshot.forEach((doc) => {
        const auditData = doc.data();
        audits.push({ 
          id: doc.id, 
          ...auditData 
        } as Audit);
      });
      
      return { success: true, data: audits };
    } catch (error: any) {
      console.error('Error getting audits for location:', error);
      return { success: false, error: error.message };
    }
  },

  // Get single audit by ID
  getAudit: async (auditId: string) => {
    try {
      const auditRef = doc(db, 'audits', auditId);
      const snapshot = await getDoc(auditRef);
      
      if (snapshot.exists()) {
        const auditData = snapshot.data();
        return { 
          success: true, 
          data: { id: snapshot.id, ...auditData } as Audit 
        };
      } else {
        return { success: false, error: 'Audit not found' };
      }
    } catch (error: any) {
      console.error('Error getting audit:', error);
      return { success: false, error: error.message };
    }
  },

  // Create audit instance
  createAuditInstance: async (
    audit: Omit<Audit, 'id' | 'createdAt' | 'updatedAt'>,
    userInfo?: UserInfo
  ) => {
    try {
      const auditsRef = collection(db, 'audits');
      const auditData: any = {
        ...audit,
        completionPercentage: audit.completionPercentage || 0,
        associatedTaskIds: audit.associatedTaskIds || [],
        status: audit.status || 'planned',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // Remove undefined values
      Object.keys(auditData).forEach((key) => {
        if (auditData[key] === undefined) {
          delete auditData[key];
        }
      });
      
      const docRef = await addDoc(auditsRef, auditData);
      
      // Log audit entry
      if (userInfo) {
        await auditLogService.log({
          action: 'audit_created',
          entityType: 'audit',
          entityId: docRef.id,
          entityName: audit.auditTemplateName || 'Audit',
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: audit.organizationId,
        });
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error creating audit instance:', error);
      return { success: false, error: error.message };
    }
  },

  // Update audit instance
  updateAudit: async (
    auditId: string,
    updates: Partial<Audit>,
    userInfo?: UserInfo
  ) => {
    try {
      const auditRef = doc(db, 'audits', auditId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      // Remove undefined values
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      
      await updateDoc(auditRef, updateData);
      
      // Log audit entry
      if (userInfo) {
        // Get audit name for logging
        const auditSnap = await getDoc(auditRef);
        const auditName = auditSnap.exists() 
          ? auditSnap.data().auditTemplateName || 'Audit'
          : 'Unknown';
        
        await auditLogService.log({
          action: 'audit_updated',
          entityType: 'audit',
          entityId: auditId,
          entityName: auditName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: updates.organizationId || auditSnap.data()?.organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating audit:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete audit instance
  deleteAudit: async (
    auditId: string,
    userInfo?: UserInfo
  ) => {
    try {
      const auditRef = doc(db, 'audits', auditId);
      await deleteDoc(auditRef);
      
      // Log audit entry
      if (userInfo) {
        await auditLogService.log({
          action: 'audit_deleted',
          entityType: 'audit',
          entityId: auditId,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting audit:', error);
      return { success: false, error: error.message };
    }
  },

  // Update audit status
  updateAuditStatus: async (
    auditId: string,
    status: Audit['status'],
    userInfo?: UserInfo
  ) => {
    try {
      const auditRef = doc(db, 'audits', auditId);
      const updateData: any = {
        status,
        updatedAt: Timestamp.now()
      };
      
      // Set startedAt if status is 'in_progress' and not already set
      if (status === 'in_progress') {
        const auditSnap = await getDoc(auditRef);
        if (auditSnap.exists() && !auditSnap.data().startedAt) {
          updateData.startedAt = Timestamp.now();
        }
      }
      
      // Set completedAt if status is 'completed'
      if (status === 'completed') {
        updateData.completedAt = Timestamp.now();
        if (userInfo) {
          updateData.completedBy = userInfo.id;
        }
      }
      
      await updateDoc(auditRef, updateData);
      
      // Log audit entry
      if (userInfo) {
        const auditSnap = await getDoc(auditRef);
        const auditName = auditSnap.exists() 
          ? auditSnap.data().auditTemplateName || 'Audit'
          : 'Unknown';
        
        await auditLogService.log({
          action: 'audit_status_updated',
          entityType: 'audit',
          entityId: auditId,
          entityName: auditName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: auditSnap.data()?.organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating audit status:', error);
      return { success: false, error: error.message };
    }
  },
};

